import { RefreshCw, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export interface SDSourceLinkFormData {
  title: string
  externalId: string
  premiumRequired: boolean
  url: string
  editorialUrl: string
  /** Where the problem originated. At most one source per problem may hold it. */
  isOriginal: boolean
}

export interface SDSourceLinkSubmitData extends SDSourceLinkFormData {
  /**
   * Whether the admin actually moved the original mark, measured against the
   * value this modal prefilled from.
   *
   * The parent cannot compute this: the prefill is keyed on the row id and
   * deliberately survives refetches, so the query cache it would compare
   * against drifts out from under an open modal. Comparing there sends an
   * untouched checkbox as an explicit write and clobbers a mark someone else
   * moved meanwhile — in either direction.
   */
  isOriginalChanged: boolean
}

interface SDSourceLinkModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: SDSourceLinkSubmitData) => Promise<void>
  problemTitle: string
  sourceName: string // Shown in the modal header
  existingSource?: {
    /** The `SystemDesignProblemSource` row id — what the prefill is keyed on. */
    id: string
    title: string
    externalId?: string | null
    premiumRequired: boolean
    url?: string | null
    editorialUrl?: string | null
    isOriginal: boolean
  } | null
  isLoading?: boolean // Show loading spinner while fetching existing source data
}

const initialFormData: SDSourceLinkFormData = {
  title: '',
  externalId: '',
  premiumRequired: false,
  url: '',
  editorialUrl: '',
  isOriginal: false,
}

export function SDSourceLinkModal({
  isOpen,
  onClose,
  onSubmit,
  problemTitle,
  sourceName,
  existingSource,
  isLoading,
}: SDSourceLinkModalProps) {
  const [formData, setFormData] =
    useState<SDSourceLinkFormData>(initialFormData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Read the freshest row without depending on its identity; synced in its own
  // effect (declared first, so it lands before the prefill below runs in the
  // same commit) rather than written during render.
  // The mark as it stood when this modal last prefilled — the baseline the
  // submit delta is measured against. Not state: nothing renders from it.
  const prefilledIsOriginalRef = useRef(false)
  const existingSourceRef = useRef(existingSource)
  useEffect(() => {
    existingSourceRef.current = existingSource
  }, [existingSource])

  // Prefill once per (open, row) — keyed on the row id, never on the object.
  // `existingSource` is a brand-new object on every fetch of
  // `getSystemDesignProblemSource`; with it in the deps, a refetch that changed
  // nothing (window focus past the 30s staleTime, or the invalidation this flow
  // now issues) silently overwrote whatever the admin had typed with the
  // persisted values.
  const existingSourceId = existingSource?.id ?? null
  // biome-ignore lint/correctness/useExhaustiveDependencies: prefilling is
  // keyed on which row the modal opened for, which the rule cannot see because
  // the body reads the row through a ref; see the comment above.
  useEffect(() => {
    const existingSource = existingSourceRef.current
    if (isOpen) {
      if (existingSource) {
        // Edit mode - populate from existing source
        setFormData({
          title: existingSource.title,
          externalId: existingSource.externalId || '',
          premiumRequired: existingSource.premiumRequired,
          url: existingSource.url || '',
          editorialUrl: existingSource.editorialUrl || '',
          isOriginal: existingSource.isOriginal,
        })
        prefilledIsOriginalRef.current = existingSource.isOriginal
      } else {
        // Create mode - default title to problem title
        setFormData({
          ...initialFormData,
          title: problemTitle,
        })
        prefilledIsOriginalRef.current = false
      }
      setError(null)
    }
  }, [isOpen, existingSourceId, problemTitle])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setIsSubmitting(true)
    setError(null)
    try {
      await onSubmit({
        ...formData,
        isOriginalChanged:
          formData.isOriginal !== prefilledIsOriginalRef.current,
      })
      setFormData(initialFormData)
      onClose()
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save source link',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData(initialFormData)
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  const isEditing = !!existingSource

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-card border-border w-full max-w-2xl rounded-lg border p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-foreground text-lg font-semibold">
            {isEditing ? `Edit ${sourceName} Link` : `Link to ${sourceName}`}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="bg-destructive/10 text-destructive mb-4 rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-12">
            <RefreshCw className="text-muted-foreground h-5 w-5 animate-spin" />
            <span className="text-muted-foreground text-sm">
              Loading source data...
            </span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Title */}
              <div>
                <label
                  htmlFor="sd-source-link-title"
                  className="text-muted-foreground mb-1 block text-sm font-medium"
                >
                  Title
                </label>
                <Input
                  id="sd-source-link-title"
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Problem title"
                />
              </div>

              {/* External ID */}
              <div>
                <label
                  htmlFor="sd-source-link-external-id"
                  className="text-muted-foreground mb-1 block text-sm font-medium"
                >
                  External ID
                </label>
                <Input
                  id="sd-source-link-external-id"
                  type="text"
                  value={formData.externalId}
                  onChange={(e) =>
                    setFormData({ ...formData, externalId: e.target.value })
                  }
                  placeholder="e.g., 1 or CH1-Q3"
                  className="font-mono"
                />
              </div>

              {/* Original */}
              <div>
                <label
                  htmlFor="sd-source-link-original"
                  className="text-muted-foreground mb-1 block text-sm font-medium"
                >
                  Original
                </label>
                <div className="flex h-9 items-center">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      id="sd-source-link-original"
                      type="checkbox"
                      checked={formData.isOriginal}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isOriginal: e.target.checked,
                        })
                      }
                      className="h-4 w-4 rounded border"
                    />
                    <span className="text-sm">Problem originated here</span>
                  </label>
                </div>
              </div>

              {/* Premium */}
              <div>
                <label
                  htmlFor="sd-source-link-premium"
                  className="text-muted-foreground mb-1 block text-sm font-medium"
                >
                  Premium
                </label>
                <div className="flex h-9 items-center">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      id="sd-source-link-premium"
                      type="checkbox"
                      checked={formData.premiumRequired}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          premiumRequired: e.target.checked,
                        })
                      }
                      className="h-4 w-4 rounded border"
                    />
                    <span className="text-sm">Premium required</span>
                  </label>
                </div>
              </div>

              {/* Problem URL */}
              <div>
                <label
                  htmlFor="sd-source-link-url"
                  className="text-muted-foreground mb-1 block text-sm font-medium"
                >
                  Problem URL
                </label>
                <Input
                  id="sd-source-link-url"
                  type="url"
                  value={formData.url}
                  onChange={(e) =>
                    setFormData({ ...formData, url: e.target.value })
                  }
                  placeholder="https://..."
                />
              </div>

              {/* Editorial URL */}
              <div>
                <label
                  htmlFor="sd-source-link-editorial-url"
                  className="text-muted-foreground mb-1 block text-sm font-medium"
                >
                  Editorial URL
                </label>
                <Input
                  id="sd-source-link-editorial-url"
                  type="url"
                  value={formData.editorialUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, editorialUrl: e.target.value })
                  }
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? isEditing
                    ? 'Saving...'
                    : 'Linking...'
                  : isEditing
                    ? 'Save Changes'
                    : 'Link Source'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
